import { GreenCheck } from '@assets/icons';
import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';
import { Colors } from '@constants/colors.constants';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid-pro';
import { AuditOfferSupplierLightDto } from '@store/audits/audit-offer-followUp/audit-proposal-suppliers/audit-proposal-suppliers.model';
import { ServiceKindTypeEnum } from '@store/settings/serviceKinds';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { finalize, switchMap } from 'rxjs';
import {
  AuditOfferGroupedSupplierOfferItemDto, AuditOfferResponseExpendedDto,
  GroupedAuditOfferSupplierDto
} from '../../../../store/audits/audit-offer/audit-offer.model';
import { auditsOfferService } from '../../../../store/audits/audit-offer/audit-offer.services';


enum DetailStatusEnum {
  ACCEPTED = 'ACCEPTED',
  NONE = 'NONE',
  DENIED = 'DENIED',
}

interface SelectSupplierGroupedProps {
  handleCloseModal: () => void;
  auditOffersIds: number[];
  selectedOffers?: AuditOfferResponseExpendedDto[];
}

const SelectSupplierGrouped = (props: SelectSupplierGroupedProps) => {
  const { handleCloseModal, auditOffersIds, selectedOffers } = props;
  const { t } = useTranslation();
  const [supplierData, setSupplierData] = useState<GroupedAuditOfferSupplierDto[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { auditId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [allSelectedSupplierRow, setAllSelectedSupplierRow] = useState<AuditOfferGroupedSupplierOfferItemDto[]>(selectedOffers?.filter((o) => o.selectedOfferId !== null).map((o) => ({auditOfferId: o.id, auditOfferSupplierId: o.selectedOfferId})) || []);

  const addSelectedSupplierRow = (auditOfferId: number, auditOfferSupplierId: number) => {
      const existingRow = allSelectedSupplierRow.find((r) => r.auditOfferId === auditOfferId);
        if (existingRow) {
            existingRow.auditOfferSupplierId = auditOfferSupplierId;
            setAllSelectedSupplierRow(allSelectedSupplierRow.map((r) => r.auditOfferId === auditOfferId ? { ...r, auditOfferSupplierId } : r));
        } else {
            setAllSelectedSupplierRow((prev) => [...prev, {auditOfferId, auditOfferSupplierId}] );
        }
  };

  const getSelectedSupplierRows = (auditOfferId: number) => allSelectedSupplierRow.find((r) => r.auditOfferId === auditOfferId)?.auditOfferSupplierId;

  useEffect(() => {
    if (auditId) {
        setIsLoading(true);
      auditsOfferService.GETGroupedAuditsOffersSuppliers({auditOfferIds: auditOffersIds ?? []}).pipe(
        finalize(() => setIsLoading(false)),
      ).subscribe({
        next: (response) => {
          setSupplierData(response);
        },
        error: () => {
          enqueueSnackbar(t('errors.genericError'), { variant: 'error' });
        },
      });
    }
  }, [auditId, t, enqueueSnackbar, auditOffersIds]);

  const items = [
    { label: 'Accepté', value: DetailStatusEnum.ACCEPTED, color: Colors.success, backgroundColor: '#58e06d33', },
    { label: 'En attente de réponse', value: DetailStatusEnum.NONE, color: Colors.warning, backgroundColor: '#F2A93B', },
    { label: 'Refusé', value: DetailStatusEnum.DENIED, color: Colors.error, backgroundColor: '#F44336', },

  ];

  const getLabels = (value: string) => {
    const item = items.find((it) => it.value === value);
    return item?.label;
  };

  const handleSelectedProposalRows = (params: any, offer: number) => {
    addSelectedSupplierRow(offer, params[0]);
  };

  const handleValidate = () => {
      setIsLoading(true);
      auditsOfferService.PutGroupedAuditsOffersSuppliers({
          auditOffers: allSelectedSupplierRow,
      }).pipe(
          finalize(() => {
              setIsLoading(false);
              handleCloseModal();
          }),
          switchMap(() => auditsOfferService.GETAuditOffersByAudit(auditId as string).pipe((
              switchMap(() => auditsOfferService.GETSelectedSuppliersByAuditId(auditId as string))
          ))),


      ).subscribe({
          next: () => {
              enqueueSnackbar(t('audits.success.offer.price-group-action'), { variant: 'success' });
              handleCloseModal();
          },
          error: () => {
              enqueueSnackbar(t('errors.genericError'), { variant: 'error' });
          },
      });
  };

  return (
    <ModalComponent
        scroll={'body'}
      aria-label="over-drawer"
      title={t('audits.OfferFormalization.selectSupplier.title')}
      handleClose={handleCloseModal}
      customColor='#FFFFFF'
      contentFullWidth
      maxWidth='xl'
      content={(
        <Stack width="100%" height={'100%'} bgcolor='#FFFFFF' justifyContent='center' alignItems='center'>
            {
                isLoading ?
                    <Stack height={'50dvh'} justifyContent={isLoading ? 'center' : undefined}>
                        <CircularProgress size={40} sx={{ alignSelf: 'center' }} />
                    </Stack>
                    :
                supplierData?.map((item) => {
                return (
                    <NewTableComponent
                        autoHeight={true}
                        loading={isLoading}
                        columns={[
                            COLUMN_TYPES.TEXT({
                                field: 'selectedSupplierName',
                                color: 'secondary',
                                label: `${item.container} - ${item.waste}`,
                                width: 400,
                            }),
                            COLUMN_TYPES.NUMBER({
                                field: 'unitCostInCents',
                                label: t('audits.OfferFormalization.selectSupplier.price'),
                                valueGetter: (params: GridValueGetterParams<any, AuditOfferSupplierLightDto>) => params.row?.unitCostInCents,
                                valueFormatter: (params) => params?.value ? `${(params?.value / 100).toFixed(2)} CHF` : '0 CHF',
                            }),
                            COLUMN_TYPES.TEXT({
                                field: item.serviceKindType,
                                color: 'secondary',
                                label: ServiceKindTypeEnum.selectItems().find((o) => o.value === item.serviceKindType)?.label,
                                width: 180,
                            }),
                            COLUMN_TYPES.TEXT({
                                field: 'serviceKind',
                                color: 'secondary',
                                label: 'Prestation',
                                width: 350,
                            }),
                            COLUMN_TYPES.CUSTOM({
                                field: 'status',
                                color: '#FFFFF',
                                label: t('audits.OfferFormalization.selectSupplier.status'),
                                renderCell: ({ value }) => (
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        sx={{
                                            gap: 1,
                                            borderRadius: '4px',
                                            padding: '4px 14px',
                                            backgroundColor: value === DetailStatusEnum.ACCEPTED ? '#58e06d33' : value === DetailStatusEnum.NONE ? alpha('#F2A93B', 0.14) : value === DetailStatusEnum.DENIED ? alpha('#E6433C', 0.14) : undefined,
                                        }}
                                    >
                                        {value === DetailStatusEnum.ACCEPTED ? <GreenCheck /> :
                                            value === DetailStatusEnum.NONE ? <AccessTimeIcon sx={{ color: '#F2A93B', width: '16px', height: '16px' }} /> :
                                                value === DetailStatusEnum.DENIED ? <CloseIcon sx={{ color: '#F44336', width: '16px', height: '16px' }} /> :
                                                    undefined}
                                        <Typography fontSize={'14px'} fontWeight={400} color={value === DetailStatusEnum.ACCEPTED ? Colors.success : value === DetailStatusEnum.NONE ? Colors.warning : Colors.error}>
                                            {getLabels(value)}
                                        </Typography>
                                    </Stack>
                                ),
                                width: 200,
                            }),
                        ]}
                        disablePagination
                        disableRowSelectionOnClick={true}
                        disableEdition={true}
                        isRowSelectable={(params) => { return params.row.status === DetailStatusEnum.ACCEPTED; }}
                        handleRowSelected={(params) => handleSelectedProposalRows(params, item.id)}
                        rowsSelected={getSelectedSupplierRows(item.id) ? [getSelectedSupplierRows(item.id)] : []}
                        fullScreen
                        data={item.auditOfferSuppliers || []}
                        singleRowSelection
                    />
                );
            })}
          <Button
            sx={{ width: '100px', alignSelf: 'center', bottom: '40px' }}
            variant="contained"
            color="primary"
            disabled={allSelectedSupplierRow.length === 0}
            onClick={handleValidate}
          >
            {t('global.validate')}
          </Button>
        </Stack>
      )}
    />
  );
};

export default SelectSupplierGrouped;
